import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { clearAllDrafts } from "../utils/autoDraft";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Bir hata oluştu. Lütfen tekrar deneyin.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
      });
      setUser(data);
    } catch (error) {
      console.debug("checkAuth: not authenticated", error?.message || error);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const login = async (email, password, opts = {}) => {
    try {
      const payload = { email, password };
      if (opts.totp_code) payload.totp_code = opts.totp_code;
      if (opts.recovery_code) payload.recovery_code = opts.recovery_code;
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        payload,
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Special: backend signals TOTP required with detail === "TOTP_REQUIRED"
      if (detail === "TOTP_REQUIRED") {
        return { success: false, totp_required: true };
      }
      return {
        success: false,
        error: formatApiErrorDetail(detail) || error.message,
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.debug("Logout network error:", error?.message || error);
    }
    // Clear all client state regardless of network result
    try {
      clearAllDrafts();
    } catch (error) {
      console.debug("Draft cleanup skipped:", error?.message || error);
    }
    setUser(false);
  };

  const logoutAll = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/sessions/logout-all`, {}, { withCredentials: true });
    } catch (error) {
      console.debug("Logout-all network error:", error?.message || error);
    }
    try {
      clearAllDrafts();
    } catch (error) {
      console.debug("Draft cleanup skipped:", error?.message || error);
    }
    setUser(false);
  };

  const contextValue = useMemo(
    () => ({ user, loading, login, logout, logoutAll, refreshUser }),
    // login/logout are stable closures within this render; explicitly
    // memoise on user/loading/refreshUser to avoid needless re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, refreshUser]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
